import { computed, effect, inject, Injectable, signal } from '@angular/core'
import { HttpService } from '@bases/http.service'
import { API_URL } from '@core/constants/constant.data'
import { LAYERS_ARRAY } from '@core/constants/layers.data'
import { HECTARE } from '@core/constants/scale.data'
import {
  LayersLoadResultsPayloadHectares,
  LayersLoadResultsPayloadNUTS,
  LayersResult,
  PersonalLayersLoadResultsPayload,
} from '@core/models/result-manager.model'
import { LayersService } from '@core/services/layers.service'
import { SelectionScaleService } from '@core/services/selection-scale.service'
import { SelectionToolService } from '@core/services/selection-tool.service'
import Keycloak from 'keycloak-js'
import { UploadedLayersService } from './layers-uploaded.service'

@Injectable({
  providedIn: 'root',
})
export class LayerResultService extends HttpService {
  private readonly _layersService = inject(LayersService)
  private readonly _selectionToolService = inject(SelectionToolService)
  private readonly _uploadedLayersService = inject(UploadedLayersService)
  private readonly _selectionScaleService = inject(SelectionScaleService)

  readonly keycloak = inject(Keycloak)
  protected _baseUrl = API_URL + '/stats/'

  indicatorExportButtonState = false
  graphicsExportButtonState = false
  controllerNutsId = new AbortController()
  controllerHectare = new AbortController()
  controllerPersonalLayer = new AbortController()

  layerLoadResultsPayloadNUTS$ = computed<LayersLoadResultsPayloadNUTS>(() => ({
    layers: this._layersService.selectedLayers$(),
    scale_level: this._selectionScaleService.currentScaleLevel$()?.displayName || '',
    nuts: this._selectionToolService.nutsIds$(),
    surface_km2: this._selectionToolService.selectedSurface$(),
  }))

  layerLoadResultsPayloadHectare$ = computed<LayersLoadResultsPayloadHectares>(() => ({
    layers: this._layersService.selectedLayers$(),
    scale_level: this._selectionScaleService.currentScaleLevel$()?.displayName || '',
    geojson_selection: this._selectionToolService.geoJsonSelection$(),
    surface_km2: this._selectionToolService.selectedSurface$(),
  }))

  personalLayerLoadResultsPayload$ = computed<PersonalLayersLoadResultsPayload>(() => ({
    // Layers are not formatted properly
    layers: this._uploadedLayersService.selectedUploadLayersMapped$() || [],
    scale_level: this._selectionScaleService.currentScaleLevel$()?.payloadScale || '',
    areas: this._selectionToolService.nutsIds$(),
    geojson_selection: this._selectionToolService.geoJsonSelection$(),
  }))

  emptyLayerResult = {
    layers: [],
  }

  isLayerResultsLoading$ = signal<boolean>(false)

  layerResultsNUTS$ = signal<LayersResult>(this.emptyLayerResult)
  layerResultsHectares$ = signal<LayersResult>(this.emptyLayerResult)
  uploadedLayerResults$ = signal<LayersResult>(this.emptyLayerResult)
  allLayerResults$ = computed<LayersResult>(() => {
    const nuts = this.layerResultsNUTS$()
    const hectares = this.layerResultsHectares$()
    const personal = this.uploadedLayerResults$().layers.map((layer) => ({
      ...layer,
      category: 'Personal and shared layers',
    }))

    return {
      layers: [...(nuts?.layers || []), ...(hectares?.layers || []), ...personal],
    }
  })

  constructor() {
    super()

    // Clear layer results on scale level change
    effect(() => {
      this._selectionScaleService.currentScaleLevel$()
      this._emptyResults()
    })

    // Clear layer results if no selected surfaces
    effect(() => {
      if (this._selectionToolService.selectedSurface$() <= 0) {
        this.controllerNutsId.abort()
        this.controllerHectare.abort()
        this.controllerPersonalLayer.abort()

        this._emptyResults()
        this.isLayerResultsLoading$.set(false)
      }
    })

    // Fetch layers NUTS/LOAD results
    effect((onCleanup) => {
      this.layerResultsNUTS$.set(this.emptyLayerResult)

      //@todo, the logic for the energy mix layer should not be hardcode or should at least be hardcoded in the backend
      if (
        this.layerLoadResultsPayloadNUTS$().nuts.length > 0 &&
        this._layersService.selectedLayers$().length > 0
      ) {
        // Electricity Mix stuff, nuts0 and emissions_hourly_csv
        const payload = this.layerLoadResultsPayloadNUTS$()
        const hasYearlyCO2 = payload.layers.includes('emissions_hourly_csv')
        const isNUTS0 = payload.scale_level === 'NUTS 0'

        if (hasYearlyCO2 && isNUTS0) {
          // Fetch both cm/layers results and electricity mix, then merge
          Promise.all([
            this.getResultsWithIds(payload),
            this.getElectricityMix({ nuts: payload.nuts }),
          ])
            .then(([result, graph]) => {
              if (result && result.layers && Array.isArray(result.layers)) {
                const co2Layer = result.layers.find(
                  (l: any) => l.id === 'emissions_hourly_csv' || l.name === 'emissions_hourly_csv',
                )
                if (co2Layer) {
                  if (!co2Layer.graphics) co2Layer.graphics = []
                  co2Layer.graphics.push(graph)
                }
              }
              this.layerResultsNUTS$.set(result)
              this.isLayerResultsLoading$.set(false)
            })
            .catch(() => {
              this.isLayerResultsLoading$.set(false)
            })
        } else {
          // If not NUTS 0 or no emissions_hourly_csv, just fetch results
          this.getResultsWithIds(payload)
            .then((result) => {
              this.layerResultsNUTS$.set(result)
              this.isLayerResultsLoading$.set(false)
            })
            .catch(() => {
              this.isLayerResultsLoading$.set(false)
            })
        }
      }
      onCleanup(() => {
        this.controllerNutsId.abort()
        this.controllerNutsId = new AbortController()
      })
    })

    // Fetch layers hectares results
    effect((onCleanup) => {
      this.layerResultsHectares$.set(this.emptyLayerResult)

      if (
        this.layerLoadResultsPayloadHectare$().geojson_selection.length > 0 &&
        this._selectionScaleService.currentScaleLevel$()?.displayName === HECTARE &&
        this._layersService.selectedLayers$().length > 0
      ) {
        this.getResultsWithMultiAreas()
      }

      onCleanup(() => {
        this.controllerHectare.abort()
        this.controllerHectare = new AbortController()
      })
    })

    // Fetch personal layers results
    effect((onCleanup) => {
      this.uploadedLayerResults$.set(this.emptyLayerResult)

      if (
        this.personalLayerLoadResultsPayload$().geojson_selection.length > 0 &&
        this.keycloak.authenticated &&
        this._uploadedLayersService.selectedLayers$().length > 0
      ) {
        this.getResultsPersonalLayers()
      }

      onCleanup(() => {
        this.controllerPersonalLayer.abort()
        this.controllerPersonalLayer = new AbortController()
      })
    })
  }

  async getResultsWithIds(payload: LayersLoadResultsPayloadNUTS): Promise<any> {
    this.isLayerResultsLoading$.set(true)
    const validLayers = []
    for (const layer of payload.layers) {
      const layerName = LAYERS_ARRAY.find((l) => l.workspacename === layer)
      if (layerName?.scales && !layerName.scales.includes(payload.scale_level)) {
        this._toasterService.openSnackBar(
          `The layer ${layerName ? layerName.name : layer} has no results in ${payload.scale_level} selection.`,
        )
      } else {
        validLayers.push(layer)
      }
    }
    if (validLayers.length === 0) {
      return Promise.resolve({ layers: [] })
    }
    const validPayload = { ...payload, layers: validLayers }
    const result = await this.POST(validPayload, this._baseUrl + 'layers/nuts-lau', {
      signal: this.controllerNutsId.signal,
    })
    this.isLayerResultsLoading$.set(false)

    return result
  }

  async getResultsWithMultiAreas() {
    this.isLayerResultsLoading$.set(true)
    const payload = this.layerLoadResultsPayloadHectare$()
    const result = await this.POST(payload, this._baseUrl + 'layers/hectares', {
      signal: this.controllerHectare.signal,
    })
    this.isLayerResultsLoading$.set(false)
    if (!result) return

    this.layerResultsHectares$.set(result)
  }

  //@todo, the personal layer route can take either nutsIds or polygons but the standard layer need different routes
  async getResultsPersonalLayers() {
    this.isLayerResultsLoading$.set(true)
    const payload = this.personalLayerLoadResultsPayload$()
    if (!payload.layers) return
    const result = await this.POST(payload, this._baseUrl + 'personal-layers', {
      signal: this.controllerPersonalLayer.signal,
    })
    this.isLayerResultsLoading$.set(false)
    if (!result) return

    this.uploadedLayerResults$.set(result)
  }

  async getElectricityMix(payload: { nuts: string[] }) {
    this.isLayerResultsLoading$.set(true)
    const result = await this.POST(payload, this._baseUrl + 'energy-mix/nuts-lau', {
      signal: this.controllerNutsId.signal,
    })
    this.isLayerResultsLoading$.set(false)
    return result
  }

  private _emptyResults() {
    this.layerResultsNUTS$.set(this.emptyLayerResult)
    this.layerResultsHectares$.set(this.emptyLayerResult)
    this.uploadedLayerResults$.set(this.emptyLayerResult)
  }
}
